import { Sites } from '@ps/platform-message-framework/common/Sites';
import { select } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { minLength, noWhiteStartOrEnd, notEqualTo, required } from '@ps/web-shared-validators';
import React, { useRef } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';

import TextInput from '../../../../TextInput/TextInput';
import RMFDropDown from '../../RMFDropDown/RMFDropDown';

import {
  TXTCLI_Answers_must_be_at_least_2_cha_ELL,
  TXTCLI_This_field_is_requiredX,
  TXTCLI_This_selection_is_requiredX,
  TXTCLI_You_canXt_use_the_same_answer__ELL,
  TXTWCL_Answer_N0,
  TXTWCL_Question_N0,
  TXTWCL_Select_a_question
} from './SecurityQuestionsFields.messages.json';
import { container, label } from './SecurityQuestionsFields.scss';

const answerMinLength = 2;
const question1FieldName = 'question1';
const question2FieldName = 'question2';
const answer1FieldName = 'answer1';
const answer2FieldName = 'answer2';

const SecurityQuestionsFields: React.FC = () => {
  const site = useSelector(select.cms.getSiteId);
  const isUSMI = site === Sites.USMI;

  const {
    input: { value: question1Value }
  } = useField(question1FieldName, { subscription: { value: true } });
  const {
    input: { value: question2Value }
  } = useField(question2FieldName, { subscription: { value: true } });
  const {
    data: { realMoneyInformation }
  } = useWizardContext();
  const {
    input: { value: answer1Value }
  } = useField(answer1FieldName, { subscription: { value: true } });

  const answer1ValueRef = useRef(answer1Value);
  const { securityQuestions } = realMoneyInformation || {};

  const getOptions = () => {
    let options = [] as { value: number; text: string }[];

    if (securityQuestions?.length) {
      options = securityQuestions.map((item) => ({
        text: item.question,
        value: item.id
      }));
    }

    return options;
  };

  const options = getOptions();
  const filterQuestions = (selectedQuestion: number) =>
    options.filter((item) => item.value !== selectedQuestion);

  const validateUniqueAnswers = (value: string) => {
    if (isUSMI && answer1ValueRef.current) {
      return notEqualTo(answer1ValueRef.current, TXTCLI_You_canXt_use_the_same_answer__ELL)(value);
    }

    return { valid: true };
  };

  return (
    <>
      <RMFDropDown
        id={question1FieldName}
        label={TXTWCL_Question_N0}
        values={{ 0: 1 }}
        options={filterQuestions(question2Value)}
        validators={[required(TXTCLI_This_selection_is_requiredX)]}
        placeholder={<Translation {...TXTWCL_Select_a_question} />}
        className={container}
      />
      <TextInput
        id={answer1FieldName}
        label={TXTWCL_Answer_N0}
        values={{ 0: 1 }}
        minLength={answerMinLength}
        validators={[
          noWhiteStartOrEnd,
          required(TXTCLI_This_field_is_requiredX),
          minLength(answerMinLength, { ...TXTCLI_Answers_must_be_at_least_2_cha_ELL })
        ]}
        onChange={(event) => {
          answer1ValueRef.current = event.target.value;
        }}
        className={container}
        checkDisabled
      />
      <RMFDropDown
        id={question2FieldName}
        label={TXTWCL_Question_N0}
        values={{ 0: 2 }}
        options={filterQuestions(question1Value)}
        validators={[required(TXTCLI_This_selection_is_requiredX)]}
        placeholder={<Translation {...TXTWCL_Select_a_question} />}
        className={container}
        labelClassName={label}
      />
      <TextInput
        id={answer2FieldName}
        label={TXTWCL_Answer_N0}
        values={{ 0: 2 }}
        minLength={answerMinLength}
        validators={[
          noWhiteStartOrEnd,
          required(TXTCLI_This_field_is_requiredX),
          minLength(answerMinLength, { ...TXTCLI_Answers_must_be_at_least_2_cha_ELL }),
          validateUniqueAnswers
        ]}
        className={container}
        checkDisabled
      />
    </>
  );
};

export default SecurityQuestionsFields;
